import { type B2bConfig, type B2bCustomer, type B2bSegment, getB2bConfig, listB2bCustomers, updateB2bConfig } from '@api/b2b';
import { DataSheet, type DataSheetColumn, SheetTabStrip } from '@components/ui/DataSheet';
import { listChannels } from '@api/channel';
import { listWarehousesForPicker } from '@api/work-order';
import { ActionIcon, Button, Checkbox, NumberInput, Select, Switch, TagsInput, Text, TextInput, Textarea } from '@mantine/core';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import CreateCustomerModal from './CreateCustomerModal';
import EditCustomerModal from './EditCustomerModal';
import styles from './b2b.module.css';
import ProductDetailsEditor from './ProductDetailsEditor';
import { Pill } from './shared';
import { ADVANCE, advanceOf, creditDaysOf, errorMessage, type ProductDetailDraft, savableDetails, termsLabel, termsPreset } from './utils';


/**
 * B2B setup: on/off, segments with their default terms and minimum order, PI validity, payment
 * verification window, default ship-from, the note customers see, and who is emailed.
 */
const SetupPanel: React.FC = () => {
    const queryClient = useQueryClient();
    const { data: config } = useQuery({ queryKey: ['b2b-config'], queryFn: getB2bConfig });
    const { data: warehouses = [] } = useQuery({ queryKey: ['warehouses-picker-internal'], queryFn: listWarehousesForPicker, staleTime: 5 * 60 * 1000 });
    const { data: channels = [] } = useQuery({ queryKey: ['channel-rows'], queryFn: listChannels, staleTime: 5 * 60 * 1000 });
    // B2B / OR channels, one entry per key: the candidates a brand already sells offline / B2B on.
    const channelOptions = [...new Map(
        channels
            .filter((c) => c.type === 'B2B' && c.model?.trim() === 'OR' && c.abbreviationKey)
            .map((c) => [c.abbreviationKey as string, { value: c.abbreviationKey as string, label: `${c.abbreviationKey}${c.name && c.name !== c.abbreviationKey ? ` (${c.name})` : ''}` }]),
    ).values()];
    const [draft, setDraft] = useState<B2bConfig | null>(null);
    const [details, setDetails] = useState<ProductDetailDraft[]>([]);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (config) {
            setDraft(config);
            setDetails(config.productDetails ?? []);
        }
    }, [config]);
    if (!draft) return <Text size="sm" c="dimmed">Loading…</Text>;

    const set = (patch: Partial<B2bConfig>) => setDraft((d) => (d ? { ...d, ...patch } : d));
    const setSegment = (i: number, patch: Partial<B2bSegment>) => set({ segments: draft.segments.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
    const recipients = (key: keyof B2bConfig['notifications']) => draft.notifications[key]?.to ?? [];
    const setRecipients = (key: keyof B2bConfig['notifications'], to: string[]) => set({ notifications: { ...draft.notifications, [key]: { to, cc: [] } } });

    const save = async () => {
        setBusy(true);
        setError(null);
        setMessage(null);
        try {
            await updateB2bConfig({
                enabled: draft.enabled,
                segments: draft.segments.map((s) => ({ ...s, key: s.key.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_') })),
                instructions: draft.instructions?.trim() || undefined,
                piValidityDays: draft.piValidityDays,
                paymentVerifySlaHours: draft.paymentVerifySlaHours,
                defaultShipFromWarehouseId: draft.defaultShipFromWarehouseId || undefined,
                dispatchRecordedInWms: !!draft.dispatchRecordedInWms,
                channelKeys: draft.channelKeys,
                notifications: draft.notifications,
                productDetails: savableDetails(details),
            });
            await queryClient.invalidateQueries({ queryKey: ['b2b-config'] });
            await queryClient.invalidateQueries({ queryKey: ['org-config'] });
            // Screens that show product details read them from these.
            await queryClient.invalidateQueries({ queryKey: ['b2b-products'] });
            await queryClient.invalidateQueries({ queryKey: ['b2b-catalog'] });
            setMessage('Saved.');
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={styles.stack}>
            <Text fw={600} size="sm">B2B Customers</Text>
            <Text size="xs" c="dimmed">
                Sell directly to distributors, retail chains and institutions: they order on a portal at your prices and schemes, you review and
                issue the PI, they pay in advance, you dispatch. Turning this on shows B2B Customers in the menu.
            </Text>
            <Switch label="Sell to B2B customers" checked={draft.enabled} onChange={(e) => set({ enabled: e.currentTarget.checked })} />

            <Text fw={600} size="sm" mt="md">Segments</Text>
            <Text size="xs" c="dimmed">Key is a short code for the segment, e.g. DISTRIBUTOR; the label is what people see.</Text>
            {draft.segments.map((s, i) => (
                <div key={i} className={styles.row}>
                    <TextInput size="xs" label="Key" value={s.key} onChange={(e) => setSegment(i, { key: e.currentTarget.value.toUpperCase() })} w={160} />
                    <TextInput size="xs" label="Label" value={s.label} onChange={(e) => setSegment(i, { label: e.currentTarget.value })} w={180} />
                    <NumberInput size="xs" label="Advance %" min={0} max={100} value={advanceOf(s.defaultTerms)} onChange={(v) => setSegment(i, { defaultTerms: termsPreset(Number(v) || 0, creditDaysOf(s.defaultTerms) || 30) })} w={110} />
                    <NumberInput size="xs" label="Credit days" min={0} disabled={advanceOf(s.defaultTerms) >= 100} value={creditDaysOf(s.defaultTerms)} onChange={(v) => setSegment(i, { defaultTerms: termsPreset(advanceOf(s.defaultTerms), Number(v) || 0) })} w={110} />
                    <NumberInput size="xs" label="Minimum order" min={0} value={s.minOrderValue ?? ''} onChange={(v) => setSegment(i, { minOrderValue: v === '' ? undefined : Number(v) })} w={140} />
                    <ActionIcon mt={20} variant="subtle" color="gray" aria-label="Remove segment" onClick={() => set({ segments: draft.segments.filter((_, j) => j !== i) })}>
                        <TrashIcon size={16} />
                    </ActionIcon>
                </div>
            ))}
            <Button size="xs" variant={ButtonVariant.SUBTLE} leftSection={<PlusIcon size={14} />} w="fit-content" onClick={() => set({ segments: [...draft.segments, { key: '', label: '', defaultTerms: ADVANCE }] })}>
                Add segment
            </Button>

            <Text fw={600} size="sm" mt="md">Orders</Text>
            <div className={styles.fieldGrid}>
                <NumberInput size="xs" label="PI valid for (days)" min={1} max={90} value={draft.piValidityDays} onChange={(v) => set({ piValidityDays: Number(v) || 7 })} />
                <NumberInput size="xs" label="Hours to verify a payment proof" min={1} max={168} value={draft.paymentVerifySlaHours} onChange={(v) => set({ paymentVerifySlaHours: Number(v) || 3 })} />
                <Select
                    size="xs"
                    label="B2B channel"
                    description="Every B2B customer order is recorded on this channel. Leave empty to create B2B Direct with the first customer."
                    inputWrapperOrder={['label', 'input', 'description', 'error']}
                    data={channelOptions}
                    value={draft.channelKeys[0] ?? null}
                    onChange={(v) => set({ channelKeys: v ? [v] : [] })}
                    searchable
                    clearable
                />
                <Select
                    size="xs"
                    label="Default ship-from warehouse"
                    data={warehouses.map((w) => ({ value: w.warehouseId, label: w.name }))}
                    value={draft.defaultShipFromWarehouseId ?? null}
                    onChange={(v) => set({ defaultShipFromWarehouseId: v ?? undefined })}
                    clearable
                />
            </div>
            <Switch
                label="Our warehouse system records B2B dispatches"
                description="On: recording a dispatch here only saves the paperwork and tells the customer; the stock is booked when your warehouse system syncs it. Off: Reparo books the stock out."
                checked={!!draft.dispatchRecordedInWms}
                onChange={(e) => set({ dispatchRecordedInWms: e.currentTarget.checked })}
            />
            <Textarea size="xs" label="Note shown to customers on the portal" autosize minRows={2} value={draft.instructions ?? ''} onChange={(e) => set({ instructions: e.currentTarget.value })} />

            <Text fw={600} size="sm" mt="md">Product details</Text>
            <ProductDetailsEditor value={details} onChange={setDetails} />

            <Text fw={600} size="sm" mt="md">Who at the brand is emailed</Text>
            <div className={styles.fieldGrid}>
                <TagsInput size="xs" label="New order placed" value={recipients('orderPlaced')} onChange={(v) => setRecipients('orderPlaced', v)} />
                <TagsInput size="xs" label="Payment proof uploaded" value={recipients('paymentSubmitted')} onChange={(v) => setRecipients('paymentSubmitted', v)} />
                <TagsInput size="xs" label="Order dispatched" value={recipients('dispatched')} onChange={(v) => setRecipients('dispatched', v)} />
            </div>
            <Text size="xs" c="dimmed">Bank details printed on the PI are set on the Brand Letterhead tab; the PI number series on Order Numbering.</Text>

            {error ? <span className={styles.error}>{error}</span> : null}
            {message ? <span className={styles.cardHint}>{message}</span> : null}
            <Button size="xs" variant={ButtonVariant.ACTION} loading={busy} onClick={() => void save()} w="fit-content">
                Save B2B settings
            </Button>
        </div>
    );
};

interface CustomerRow extends B2bCustomer {
    terms: string;
}

/** Every B2B customer with Add and Edit: the one place customer records are maintained. */
const CustomersPanel: React.FC<{ config: B2bConfig | undefined; editId: string | null; onEditClosed: () => void }> = ({ config, editId, onEditClosed }) => {
    const { data: customers = [], isLoading } = useQuery({ queryKey: ['b2b-customers'], queryFn: listB2bCustomers });
    const [showInactive, setShowInactive] = useState(false);
    const [editing, setEditing] = useState<string | null>(editId);
    const [adding, setAdding] = useState(false);
    useEffect(() => setEditing(editId), [editId]);

    const rows: CustomerRow[] = customers.filter((c) => showInactive || c.status !== 'INACTIVE').map((c) => ({ ...c, terms: termsLabel(c.paymentTerms) }));
    const columns: DataSheetColumn<CustomerRow>[] = [
        { key: 'name', header: 'Customer', width: 260, sticky: true },
        { key: 'segmentLabel', header: 'Segment', width: 140, render: (v: string | null, r) => v ?? r.segment },
        { key: 'gstin', header: 'GSTIN', width: 170, render: (v: string | null) => v ?? '—' },
        { key: 'terms', header: 'Payment terms', width: 220 },
        { key: 'status', header: 'Status', width: 110, render: (v: string) => <Pill label={v} tone={v === 'ACTIVE' ? 'positive' : v === 'BLOCKED' ? 'critical' : 'muted'} /> },
        {
            key: 'customerId',
            header: '',
            width: 90,
            render: (id: string) => (
                <Button size="xs" variant={ButtonVariant.SUBTLE} onClick={() => setEditing(id)}>
                    Edit
                </Button>
            ),
        },
    ];

    return (
        <div className={styles.stack}>
            <DataSheet<CustomerRow>
                title="Customers"
                subtitle={`${rows.length} customer${rows.length === 1 ? '' : 's'}`}
                data={rows}
                columns={columns}
                getRowKey={(r) => r.customerId}
                isLoading={isLoading}
                maxHeight="auto"
                emptyState="No B2B customers yet."
                toolbarControls={
                    <div className={styles.row}>
                        <Checkbox size="xs" label="Show inactive" checked={showInactive} onChange={(e) => setShowInactive(e.currentTarget.checked)} />
                        <Button size="xs" variant={ButtonVariant.ACTION} leftSection={<PlusIcon size={14} />} disabled={!config?.segments.length} onClick={() => setAdding(true)}>
                            Add customer
                        </Button>
                    </div>
                }
            />
            {!config?.segments.length ? <span className={styles.muted}>Add a segment on the Setup tab before adding customers.</span> : null}
            {adding && config ? <CreateCustomerModal config={config} onClose={() => setAdding(false)} onCreated={() => setAdding(false)} /> : null}
            {editing && config ? (
                <EditCustomerModal
                    customerId={editing}
                    config={config}
                    onClose={() => {
                        setEditing(null);
                        onEditClosed();
                    }}
                />
            ) : null}
        </div>
    );
};

/**
 * Settings → B2B Customers: Setup (brand-wide B2B settings) and Customers (every customer record,
 * added and edited here). `?edit=<customerId>` opens that customer's edit form on Customers.
 */
const B2bSettingsTab: React.FC = () => {
    const [params, setParams] = useSearchParams();
    const editId = params.get('edit');
    const [tab, setTab] = useState(editId ? 'customers' : 'setup');
    const { data: config } = useQuery({ queryKey: ['b2b-config'], queryFn: getB2bConfig });
    return (
        <div className={styles.stack}>
            <div className={`${styles.sheet} ${styles.noShrink}`}>
                <SheetTabStrip
                    tabs={[
                        { id: 'setup', label: 'Setup' },
                        { id: 'customers', label: 'Customers' },
                    ]}
                    activeTab={tab}
                    onTabChange={setTab}
                />
            </div>
            {tab === 'setup' ? (
                <SetupPanel />
            ) : (
                <CustomersPanel
                    config={config}
                    editId={editId}
                    onEditClosed={() => {
                        if (!editId) return;
                        const next = new URLSearchParams(params);
                        next.delete('edit');
                        setParams(next, { replace: true });
                    }}
                />
            )}
        </div>
    );
};

export default B2bSettingsTab;
